import { useEffect, useState, type ReactNode } from 'react';

/**
 * A grid on a desktop, a list of cards on a phone.
 *
 * Eight tables in this product sit inside an `overflow-x-auto` with a stated
 * minimum width between 544 and 832 pixels — on a 390px phone, between 1.4 and
 * 2.1 screens wide, with controls shrunk to around 32 pixels so the columns
 * would fit. Scrolling a form sideways to reach the field you are filling in is
 * not a form.
 *
 * So below `md` the same data is one card per row, each column becoming a
 * labelled full-size control. `Sheet` already makes this argument for lists —
 * "a real `<table>` fights that" — and this is the same argument for the
 * tables that genuinely need to be grids when there is room.
 *
 * **One tree at a time, not two hidden by CSS.** These cells hold inputs, and
 * rendering both shapes would put two of every control in the document: two
 * focus stops, two things for a screen reader to find, and twice the DOM for a
 * thirty-row matrix. It is chosen from a media query instead, read
 * synchronously on first render so nothing flashes the wrong shape.
 *
 * `mode` decides which phone shape, and it is a per-screen judgement rather
 * than a default:
 *
 * - `rows` transposes — a card per row. Right when the column count grows with
 *   a club's own config, or when the question is about one row at a time.
 * - `compact` keeps the grid and freezes the first column. Right when the
 *   question is about comparing rows at a glance — a minutes plan answers "who
 *   has had nothing yet" with a column of empty boxes, and a card per player
 *   destroys exactly that (D-048).
 */
export type MatrixColumn<T> = {
  readonly key: string;
  /**
   * The column heading. A node rather than a string because a heading is
   * sometimes a control — the minutes plan's periods are editable ranges, and
   * splitting that into a second component would mean two places that know how
   * wide a period column is.
   */
  readonly label: ReactNode;
  /**
   * One control, or one value. On a phone the card wraps it in the column's
   * `<label>`, which is what associates the two without an id to thread — so a
   * cell holding two controls wants two columns.
   */
  readonly cell: (row: T, index: number) => ReactNode;
  /**
   * Names the row on a phone, as the card's heading rather than a labelled
   * field. Exactly one column should claim it — usually the day or the player.
   */
  readonly heading?: boolean;
  readonly align?: 'start' | 'end';
  readonly width?: string;
};

/** True while the viewport is at least `query`, without flashing on first paint. */
export function useAtLeast(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Tailwind's `md`. Kept here so the component and the tests agree on one number. */
export const MATRIX_BREAKPOINT = '(min-width: 48rem)';

export function Matrix<T>({
  rows,
  columns,
  rowKey,
  mode = 'rows',
  caption,
  empty,
}: {
  rows: readonly T[];
  columns: readonly MatrixColumn<T>[];
  rowKey: (row: T, index: number) => string;
  mode?: 'rows' | 'compact';
  /** Describes the grid for a screen reader; never rendered. */
  caption?: string;
  empty?: ReactNode;
}) {
  const wide = useAtLeast(MATRIX_BREAKPOINT);

  if (rows.length === 0) return <>{empty ?? null}</>;

  if (wide || mode === 'compact') {
    return (
      <div className="overflow-x-auto">
        {/*
          `compact` lays out fixed, because under `auto` a stated width is only
          a suggestion: one long surname took the frozen name column to 280px
          and pushed every period off the screen it was supposed to be frozen
          beside.
        */}
        <table className={`w-full text-meta ${mode === 'compact' ? 'table-fixed' : ''}`}>
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr className="border-b border-line text-ink-faint">
              {columns.map((col, c) => (
                <th
                  key={col.key}
                  scope="col"
                  style={col.width ? { width: col.width } : undefined}
                  className={`px-2 py-2 font-normal ${col.align === 'end' ? 'text-end' : 'text-start'} ${
                    // The name column stays put while the rest scrolls, which is
                    // what makes a compact grid readable rather than merely narrow.
                    mode === 'compact' && c === 0
                      ? 'sticky start-0 z-10 bg-surface'
                      : ''
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row, i) => (
              <tr key={rowKey(row, i)}>
                {columns.map((col, c) => (
                  <td
                    key={col.key}
                    className={`px-2 py-1.5 ${mode === 'compact' ? 'overflow-hidden' : ''} ${col.align === 'end' ? 'text-end' : ''} ${
                      mode === 'compact' && c === 0
                        ? 'sticky start-0 z-10 bg-surface'
                        : ''
                    }`}
                    style={col.width ? { width: col.width } : undefined}
                  >
                    {col.cell(row, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const heading = columns.find((c) => c.heading);
  const rest = columns.filter((c) => !c.heading);

  return (
    <ul className="grid gap-3">
      {rows.map((row, i) => (
        <li
          key={rowKey(row, i)}
          className="rounded-(--radius-control) border border-line bg-surface-sunken p-3"
        >
          {heading ? (
            <h4 className="tabular mb-2 font-condensed text-subhead font-semibold">
              {heading.cell(row, i)}
            </h4>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            {rest.map((col) => (
              <label key={col.key} className="grid gap-1">
                <span className="text-meta text-ink-muted">{col.label}</span>
                {col.cell(row, i)}
              </label>
            ))}
          </div>
        </li>
      ))}
    </ul>
  );
}
